// Relocation-specific workflow templates for Spanish immigration
const RELOCATION_WORKFLOWS = {
  NIE_APPLICATION: {
    title: 'NIE Application Process',
    description: 'Complete NIE application for Spanish residency',
    category: 'immigration',
    estimatedDuration: '2-4 weeks',
    steps: [
      {
        title: 'Gather Required Documents',
        description: 'Collect passport, photos, application form, and supporting documents',
        order: 1,
        estimatedDuration: '1-2 days',
        requiredDocuments: [
          'Passport (valid for at least 6 months)',
          '2 recent passport photos',
          'Completed EX-15 form',
          'Proof of residence in Spain',
          'Proof of economic means'
        ],
        links: [
          { title: 'EX-15 Form Download', url: 'https://www.policia.es/_es/descarga.php' },
          { title: 'Appointment Booking', url: 'https://sede.policia.gob.es/' }
        ]
      },
      {
        title: 'Book Police Appointment',
        description: 'Schedule appointment at local police station',
        order: 2,
        estimatedDuration: '1 day',
        requiredDocuments: ['All gathered documents'],
        links: [
          { title: 'Appointment System', url: 'https://sede.policia.gob.es/' }
        ]
      },
      {
        title: 'Submit Application',
        description: 'Attend appointment and submit NIE application',
        order: 3,
        estimatedDuration: '2-3 hours',
        requiredDocuments: ['All documents', 'Payment receipt'],
        links: []
      },
      {
        title: 'Collect NIE Certificate',
        description: 'Return to collect NIE certificate (usually 2-4 weeks later)',
        order: 4,
        estimatedDuration: '30 minutes',
        requiredDocuments: ['Receipt from application'],
        links: []
      }
    ]
  },

  TIE_CARD: {
    title: 'TIE Card Application',
    description: 'Apply for Spanish Foreigner Identity Card (TIE)',
    category: 'immigration',
    estimatedDuration: '4-6 weeks',
    steps: [
      {
        title: 'Obtain NIE Number',
        description: 'First obtain NIE number if not already available',
        order: 1,
        estimatedDuration: '2-4 weeks',
        requiredDocuments: ['Passport', 'Visa', 'Application form'],
        links: []
      },
      {
        title: 'Gather TIE Documents',
        description: 'Collect all required documents for TIE application',
        order: 2,
        estimatedDuration: '1-2 days',
        requiredDocuments: [
          'Passport with valid visa',
          'NIE certificate',
          'Completed EX-17 form',
          '2 passport photos',
          'Proof of address in Spain',
          'Medical certificate (if required)'
        ],
        links: [
          { title: 'EX-17 Form', url: 'https://www.policia.es/_es/descarga.php' }
        ]
      },
      {
        title: 'Book TIE Appointment',
        description: 'Schedule appointment for TIE application',
        order: 3,
        estimatedDuration: '1 day',
        requiredDocuments: ['All gathered documents'],
        links: [
          { title: 'Appointment Booking', url: 'https://sede.policia.gob.es/' }
        ]
      },
      {
        title: 'Submit TIE Application',
        description: 'Attend appointment and submit TIE application',
        order: 4,
        estimatedDuration: '1-2 hours',
        requiredDocuments: ['All documents', 'Payment receipt'],
        links: []
      },
      {
        title: 'Collect TIE Card',
        description: 'Return to collect physical TIE card',
        order: 5,
        estimatedDuration: '30 minutes',
        requiredDocuments: ['Receipt from application'],
        links: []
      }
    ]
  },

  EMPADRONAMIENTO: {
    title: 'Empadronamiento Registration',
    description: 'Register at local town hall (Padrón Municipal)',
    category: 'legal',
    estimatedDuration: '1-2 weeks',
    steps: [
      {
        title: 'Gather Documents',
        description: 'Collect required documents for registration',
        order: 1,
        estimatedDuration: '1 day',
        requiredDocuments: [
          'Passport or NIE',
          'Rental contract or property deed',
          'Utility bill (electricity, water, gas)',
          'Completed registration form'
        ],
        links: []
      },
      {
        title: 'Book Town Hall Appointment',
        description: 'Schedule appointment at local town hall',
        order: 2,
        estimatedDuration: '1 day',
        requiredDocuments: ['All gathered documents'],
        links: []
      },
      {
        title: 'Attend Registration',
        description: 'Complete registration at town hall',
        order: 3,
        estimatedDuration: '1 hour',
        requiredDocuments: ['All documents'],
        links: []
      },
      {
        title: 'Receive Certificate',
        description: 'Obtain empadronamiento certificate',
        order: 4,
        estimatedDuration: '15 minutes',
        requiredDocuments: ['Receipt from registration'],
        links: []
      }
    ]
  },

  HOUSING_SEARCH: {
    title: 'Housing Search Process',
    description: 'Find and secure accommodation in Spain',
    category: 'housing',
    estimatedDuration: '2-6 weeks',
    steps: [
      {
        title: 'Define Requirements',
        description: 'Determine budget, location, and housing preferences',
        order: 1,
        estimatedDuration: '1 day',
        requiredDocuments: ['Budget plan', 'Location preferences'],
        links: []
      },
      {
        title: 'Research Areas',
        description: 'Research neighborhoods and housing options',
        order: 2,
        estimatedDuration: '3-5 days',
        requiredDocuments: ['Area research notes'],
        links: [
          { title: 'Idealista', url: 'https://www.idealista.com' },
          { title: 'Fotocasa', url: 'https://www.fotocasa.es' },
          { title: 'Pisos.com', url: 'https://www.pisos.com' }
        ]
      },
      {
        title: 'Schedule Viewings',
        description: 'Book property viewings',
        order: 3,
        estimatedDuration: '1-2 days',
        requiredDocuments: ['Viewing schedule'],
        links: []
      },
      {
        title: 'Visit Properties',
        description: 'Attend property viewings',
        order: 4,
        estimatedDuration: '1-2 weeks',
        requiredDocuments: ['Property evaluation forms'],
        links: []
      },
      {
        title: 'Submit Application',
        description: 'Apply for selected property',
        order: 5,
        estimatedDuration: '1 day',
        requiredDocuments: [
          'Application form',
          'Passport/NIE copy',
          'Employment contract',
          'Bank statements',
          'Deposit payment'
        ],
        links: []
      },
      {
        title: 'Sign Contract',
        description: 'Sign rental agreement',
        order: 6,
        estimatedDuration: '1 day',
        requiredDocuments: ['Signed contract', 'Deposit receipt'],
        links: []
      }
    ]
  },

  SCHOOL_ENROLLMENT: {
    title: 'School Enrollment Process',
    description: 'Enroll children in Spanish schools',
    category: 'education',
    estimatedDuration: '2-4 weeks',
    steps: [
      {
        title: 'Research Schools',
        description: 'Research schools in your area',
        order: 1,
        estimatedDuration: '3-5 days',
        requiredDocuments: ['School research notes'],
        links: [
          { title: 'Ministry of Education', url: 'https://www.educacionyfp.gob.es' }
        ]
      },
      {
        title: 'Gather Documents',
        description: 'Collect required enrollment documents',
        order: 2,
        estimatedDuration: '1-2 days',
        requiredDocuments: [
          'Child\'s passport',
          'Birth certificate (translated)',
          'Previous school records',
          'Vaccination records',
          'Proof of residence',
          'Parent\'s NIE/passport'
        ],
        links: []
      },
      {
        title: 'Submit Application',
        description: 'Submit school enrollment application',
        order: 3,
        estimatedDuration: '1 day',
        requiredDocuments: ['All gathered documents'],
        links: []
      },
      {
        title: 'Attend Interview',
        description: 'Attend school interview/assessment',
        order: 4,
        estimatedDuration: '1-2 hours',
        requiredDocuments: ['Interview confirmation'],
        links: []
      },
      {
        title: 'Complete Enrollment',
        description: 'Finalize enrollment and receive confirmation',
        order: 5,
        estimatedDuration: '1 day',
        requiredDocuments: ['Enrollment confirmation'],
        links: []
      }
    ]
  }
};

export default RELOCATION_WORKFLOWS;

